/**
 * Format date to Italian format (DD-MM-YYYY)
 */
export const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  
  // Remove time part if exists
  const dateOnly = dateStr.split("T")[0];
  const [year, month, day] = dateOnly.split("-");
  
  return `${day}-${month}-${year}`;
};

/**
 * Format date to ISO format for input fields (YYYY-MM-DD)
 */
export const formatDateForInput = (dateStr: string): string => {
  if (!dateStr) return "";
  
  // If already in YYYY-MM-DD format
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateStr;
  }
  
  // If in DD-MM-YYYY format
  if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
    const [day, month, year] = dateStr.split("-");
    return `${year}-${month}-${day}`;
  }
  
  return dateStr.split("T")[0];
};

/**
 * Format currency to Italian format (€ X.XXX,XX)
 * Supports dual currency display for non-EUR currencies
 */
export const formatCurrency = (
  amount: number | null | undefined,
  originalAmount?: number | null,
  originalCurrency?: string | null
): string => {
  if (amount === null || amount === undefined) return "N/A";
  
  const eurFormatted = new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  
  // If original currency is provided and different from EUR, show both
  if (originalCurrency && originalCurrency !== "EUR" && originalAmount !== null && originalAmount !== undefined) {
    try {
      const originalFormatted = new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: originalCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(originalAmount);
      
      return `${eurFormatted} (${originalFormatted})`;
    } catch (error) {
      // If currency code is invalid, fallback to showing just EUR + amount
      console.warn(`Invalid currency code: ${originalCurrency}`, error);
      return `${eurFormatted} (${originalAmount.toFixed(2)} ${originalCurrency})`;
    }
  }
  
  return eurFormatted;
};

/**
 * Format time to HH:MM
 */
export const formatTime = (timeStr: string): string => {
  if (!timeStr) return "";
  // If it's already in HH:MM format, return as is
  if (timeStr.match(/^\d{2}:\d{2}$/)) return timeStr;
  // If it's a full datetime, extract the time part
  const timePart = timeStr.split("T")[1] || timeStr;
  return timePart.substring(0, 5); // Get HH:MM
};

/**
 * Format date for display in modals (Italian short format)
 */
export const formatDateShort = (dateStr: string): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("it-IT", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};
